// Inloggningen (designen Web-Logga-in). I AWS med e-post, lösenord och en kod
// från en app (ADR 0083). Lokalt väljer man exempelbyråns ägare eller
// medarbetare (ADR 0062).
import { useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router';
import { useAuth } from '../../auth/auth.js';
import { LOKALA_ROLLER, type LokalRoll } from '../../auth/lokal.js';
import { Marke } from '../../layout/ikoner.js';
import { CognitoInloggning } from './Cognito.js';

/** Bara sökvägar inom kontot, så att länken inte kan skicka vidare till en annan webbplats. */
export function sakerTill(till: string | null): string {
  return till && till.startsWith('/') && !till.startsWith('//') ? till : '/';
}

function LokalInloggning() {
  const { inloggning, loggaInLokalt } = useAuth();
  const [vantar, setVantar] = useState<LokalRoll | null>(null);
  const [fel, setFel] = useState<string | null>(null);

  async function logga(roll: LokalRoll) {
    setFel(null);
    setVantar(roll);
    try {
      await loggaInLokalt(roll);
    } catch (err) {
      setFel(err instanceof Error ? err.message : 'Inloggningen misslyckades.');
    } finally {
      setVantar(null);
    }
  }

  const laddar = vantar !== null || inloggning.status === 'laddar';
  return (
    <>
      <h1>Logga in</h1>
      <p>
        Lokal inloggning för utveckling. Välj vem du vill vara i exempelbyrån Älvsäng Redovisning.
      </p>
      {fel && (
        <div className="meddelande" role="alert">
          {fel}
        </div>
      )}
      {inloggning.status === 'fel' && (
        <div className="meddelande" role="alert">
          API:t svarar inte. Starta det med pnpm dev och försök igen.
        </div>
      )}
      {LOKALA_ROLLER.map(({ roll, etikett }, i) => (
        <button
          key={roll}
          type="button"
          className={`btn ${i === 0 ? 'btn-p' : 'btn-s'}`}
          disabled={laddar}
          aria-busy={vantar === roll}
          onClick={() => void logga(roll)}
        >
          {vantar === roll ? 'Loggar in …' : etikett}
        </button>
      ))}
    </>
  );
}

export function LoggaIn() {
  const { inloggning, satt, loggaInMed } = useAuth();
  const [params] = useSearchParams();

  useEffect(() => {
    document.title = 'Logga in – LeadLedger';
  }, []);

  if (inloggning.status === 'inloggad') {
    return <Navigate to={sakerTill(params.get('till'))} replace />;
  }

  return (
    <main className="inloggning">
      <span className="marke">
        <Marke storlek={34} />
        LeadLedger
      </span>
      <div className="kort inloggning-kort">
        {satt.typ === 'laddar' && <p role="status">Laddar …</p>}
        {satt.typ === 'fel' && (
          <>
            <h1>Logga in</h1>
            <div className="meddelande" role="alert">
              Inloggningen går inte att nå just nu. Försök igen om en stund.
            </div>
            <button type="button" className="btn btn-s" onClick={satt.forsokIgen}>
              Försök igen
            </button>
          </>
        )}
        {satt.typ === 'lokal' && <LokalInloggning />}
        {satt.typ === 'cognito' && <CognitoInloggning cognito={satt.cognito} onKlar={loggaInMed} />}
      </div>
      {/* Byråer registrerar sig själva och börjar i förhandsvisning (ADR 0106). */}
      <p className="inloggning-ny">
        Ny byrå? <Link to="/skapa-konto">Skapa konto</Link>
      </p>
    </main>
  );
}
